/**
 * Injected Wallet Support (MetaMask, Rabby, Trust Wallet, etc.)
 * Uses window.ethereum for any browser extension wallet
 */

import {
  createPublicClient,
  createWalletClient,
  custom,
  http,
  type Address,
  type Hex,
  type PublicClient,
  type WalletClient,
  type Chain,
  encodeFunctionData,
} from 'viem';
import { getChain, CONTRACTS, USDC_ABI, MINE_OR_DIE_ABI, ENTRY_FEE, PAYMASTER_URL } from './config.js';

// Wallet state
let walletClient: WalletClient | null = null;
let publicClient: PublicClient | null = null;
let connectedAddress: Address | null = null;
let currentChain: Chain | null = null;
let activeProvider: EthereumProvider | null = null;

// Whether to use gas sponsorship (EIP-5792 wallet_sendCalls)
let useSponsorship = false;

// Ethereum provider interface
interface EthereumProvider {
  isMetaMask?: boolean;
  isRabby?: boolean;
  isTrust?: boolean;
  isCoinbaseWallet?: boolean;
  providers?: EthereumProvider[];
  request: (args: { method: string; params?: any[] }) => Promise<any>;
  on: (event: string, callback: (...args: any[]) => void) => void;
  removeListener: (event: string, callback: (...args: any[]) => void) => void;
}

// Extend window type for ethereum
declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}

/**
 * Get the MetaMask provider specifically (handles multi-wallet scenarios)
 */
function getMetaMaskProvider(): EthereumProvider | null {
  if (!window.ethereum) return null;

  // If providers array exists (multiple wallets), find MetaMask
  if (window.ethereum.providers?.length) {
    const metamask = window.ethereum.providers.find(p => p.isMetaMask && !p.isCoinbaseWallet);
    if (metamask) return metamask;
  }

  // Single provider scenario
  if (window.ethereum.isMetaMask) {
    return window.ethereum;
  }

  // Fallback to whatever is available
  return window.ethereum;
}

/**
 * Check if an injected wallet (MetaMask, etc.) is available
 */
export function isInjectedWalletAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.ethereum;
}

/**
 * Get the name of the detected injected wallet
 */
export function getInjectedWalletName(): string {
  if (typeof window === 'undefined' || !window.ethereum) {
    return 'No Wallet';
  }
  if (window.ethereum.isMetaMask) return 'MetaMask';
  if (window.ethereum.isRabby) return 'Rabby';
  if (window.ethereum.isTrust) return 'Trust Wallet';
  if (window.ethereum.isCoinbaseWallet) return 'Coinbase Wallet';
  return 'Browser Wallet';
}

/**
 * Check if injected wallet is connected
 */
export function isInjectedWalletConnected(): boolean {
  return connectedAddress !== null;
}

/**
 * Get connected address
 */
export function getInjectedWalletAddress(): Address | null {
  return connectedAddress;
}

/**
 * Connect to injected wallet (MetaMask, etc.)
 */
export async function connectInjectedWallet(): Promise<Address> {
  // Get the specific provider (handles multi-wallet scenarios)
  const provider = getMetaMaskProvider();
  if (!provider) {
    throw new Error('No wallet extension found. Please install MetaMask or another wallet.');
  }

  const chain = getChain();
  console.log('Connecting to injected wallet, target chain:', chain.name, 'id:', chain.id);
  console.log('Provider detected:', {
    isMetaMask: provider.isMetaMask,
    isRabby: provider.isRabby,
    isCoinbaseWallet: provider.isCoinbaseWallet,
    hasMultipleProviders: !!window.ethereum?.providers?.length,
  });

  // Request accounts
  console.log('Requesting accounts from wallet...');
  try {
    const accounts = (await provider.request({
      method: 'eth_requestAccounts',
    })) as Address[];

    if (!accounts || accounts.length === 0) {
      throw new Error('No accounts returned from wallet');
    }

    connectedAddress = accounts[0];
    currentChain = chain;
    console.log('Got account:', connectedAddress);
  } catch (requestError: any) {
    console.error('Account request error:', requestError);
    // Handle specific MetaMask errors
    if (requestError.message?.includes('selectExtension') || requestError.message?.includes('Unexpected error')) {
      throw new Error('Wallet conflict detected. Try disabling other wallet extensions or use MetaMask directly.');
    }
    throw requestError;
  }

  // Create viem clients using the specific provider
  walletClient = createWalletClient({
    account: connectedAddress,
    chain,
    transport: custom(provider),
  });

  publicClient = createPublicClient({
    chain,
    transport: http(),
  });

  // Switch to correct chain
  console.log('Switching to chain:', chain.id);
  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: `0x${chain.id.toString(16)}` }],
    });
    console.log('Chain switch successful');
  } catch (switchError: any) {
    console.log('Chain switch error:', switchError.code, switchError.message);
    // Chain not added, try to add it
    if (switchError.code === 4902) {
      console.log('Chain not found, adding chain...');
      await provider.request({
        method: 'wallet_addEthereumChain',
        params: [
          {
            chainId: `0x${chain.id.toString(16)}`,
            chainName: chain.name,
            nativeCurrency: chain.nativeCurrency,
            rpcUrls: [chain.rpcUrls.default.http[0]],
            blockExplorerUrls: chain.blockExplorers
              ? [chain.blockExplorers.default.url]
              : undefined,
          },
        ],
      });
      console.log('Chain added successfully');
    } else if (switchError.code === 4001) {
      // User rejected the chain switch
      throw new Error('Please switch to Base Sepolia network in MetaMask');
    } else {
      throw switchError;
    }
  }

  // Store provider reference and listen for account changes
  activeProvider = provider;
  provider.on('accountsChanged', handleAccountsChanged);
  provider.on('chainChanged', handleChainChanged);

  console.log(`${getInjectedWalletName()} connected:`, connectedAddress);
  return connectedAddress;
}

/**
 * Handle account changes
 */
function handleAccountsChanged(accounts: Address[]): void {
  if (accounts.length === 0) {
    // User disconnected
    disconnectInjectedWallet();
  } else if (accounts[0] !== connectedAddress) {
    connectedAddress = accounts[0];
    console.log('Account changed to:', connectedAddress);
  }
}

/**
 * Handle chain changes
 */
function handleChainChanged(_chainId: string): void {
  // Reload the page on chain change (recommended by MetaMask)
  window.location.reload();
}

/**
 * Disconnect injected wallet
 */
export function disconnectInjectedWallet(): void {
  if (activeProvider) {
    activeProvider.removeListener('accountsChanged', handleAccountsChanged);
    activeProvider.removeListener('chainChanged', handleChainChanged);
  }
  walletClient = null;
  publicClient = null;
  connectedAddress = null;
  currentChain = null;
  activeProvider = null;
  console.log('Injected wallet disconnected');
}

/**
 * Get USDC balance in wallet
 */
export async function getInjectedWalletUSDCBalance(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.usdc) {
    return 0n;
  }

  const balance = await publicClient.readContract({
    address: CONTRACTS.usdc,
    abi: USDC_ABI,
    functionName: 'balanceOf',
    args: [connectedAddress],
  });

  return balance;
}

// ============ Gas Sponsorship Support ============

/**
 * Check if gas sponsorship is available for injected wallet
 */
export function isInjectedSponsorshipAvailable(): boolean {
  return !!PAYMASTER_URL;
}

/**
 * Enable/disable gas sponsorship for injected wallet
 */
export function setInjectedSponsorship(enabled: boolean): void {
  useSponsorship = enabled && isInjectedSponsorshipAvailable();
}

/**
 * Check if sponsorship is currently enabled
 */
export function isInjectedSponsorshipEnabled(): boolean {
  return useSponsorship;
}

/**
 * Send sponsored transaction via EIP-5792 wallet_sendCalls
 */
async function sendSponsoredCall(
  to: Address,
  data: Hex
): Promise<Hex> {
  if (!activeProvider || !connectedAddress || !PAYMASTER_URL) {
    throw new Error('Wallet not connected or paymaster not configured');
  }

  try {
    const result = await activeProvider.request({
      method: 'wallet_sendCalls',
      params: [{
        version: '1.0',
        from: connectedAddress,
        calls: [{ to, data, value: '0x0' }],
        capabilities: {
          paymasterService: {
            url: PAYMASTER_URL,
          },
        },
      }],
    });

    const hash = typeof result === 'string' ? result : (result as any).id || (result as any).hash;

    if (publicClient) {
      await publicClient.waitForTransactionReceipt({ hash: hash as Hex });
    }

    return hash as Hex;
  } catch (e: any) {
    if (e.code === 4200 || e.message?.includes('not supported')) {
      console.warn('wallet_sendCalls not supported, gas sponsorship unavailable');
      useSponsorship = false;
      throw new Error('Your wallet does not support gas sponsorship. You will need ETH for gas.');
    }
    throw e;
  }
}

// ============ MineOrDie Contract Functions ============

/**
 * Get player's game balance (funded in MineOrDie contract)
 */
export async function getInjectedGameBalance(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  const balance = await publicClient.readContract({
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'balances',
    args: [connectedAddress],
  });

  return balance;
}

/**
 * Get player's available balance (balance - wagered)
 */
export async function getInjectedAvailableBalance(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  const available = await publicClient.readContract({
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'availableBalance',
    args: [connectedAddress],
  });

  return available;
}

/**
 * Get player's currently wagered amount
 */
export async function getInjectedWageredAmount(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  const wagered = await publicClient.readContract({
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'currentlyWagered',
    args: [connectedAddress],
  });

  return wagered;
}

/**
 * Check USDC allowance for MineOrDie contract
 */
async function getInjectedMineOrDieAllowance(): Promise<bigint> {
  if (!publicClient || !connectedAddress || !CONTRACTS.usdc || !CONTRACTS.mineOrDie) {
    return 0n;
  }

  const allowance = await publicClient.readContract({
    address: CONTRACTS.usdc,
    abi: USDC_ABI,
    functionName: 'allowance',
    args: [connectedAddress, CONTRACTS.mineOrDie],
  });

  return allowance;
}

/**
 * Approve USDC for MineOrDie contract
 */
async function approveUSDCForMineOrDie(amount: bigint): Promise<Hex> {
  if (!walletClient || !connectedAddress || !CONTRACTS.usdc || !CONTRACTS.mineOrDie || !currentChain) {
    throw new Error('Wallet not connected or contracts not configured');
  }

  const hash = await walletClient.writeContract({
    chain: currentChain,
    account: connectedAddress,
    address: CONTRACTS.usdc,
    abi: USDC_ABI,
    functionName: 'approve',
    args: [CONTRACTS.mineOrDie, amount],
  });

  console.log('USDC approval for MineOrDie tx:', hash);

  if (publicClient) {
    await publicClient.waitForTransactionReceipt({ hash });
  }

  return hash;
}

/**
 * Fund game account with USDC
 */
export async function fundInjectedGameAccount(amount: bigint): Promise<Hex> {
  if (!walletClient || !connectedAddress || !CONTRACTS.mineOrDie || !currentChain) {
    throw new Error('Wallet not connected');
  }

  // Check allowance first
  const allowance = await getInjectedMineOrDieAllowance();
  if (allowance < amount) {
    console.log('Insufficient allowance, approving USDC...');
    await approveUSDCForMineOrDie(amount);
  }

  const hash = await walletClient.writeContract({
    chain: currentChain,
    account: connectedAddress,
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'fund',
    args: [amount],
  });

  console.log('Fund tx:', hash);

  if (publicClient) {
    await publicClient.waitForTransactionReceipt({ hash });
  }

  return hash;
}

/**
 * Fund game account with gas sponsorship (if enabled)
 */
export async function fundInjectedGameAccountSponsored(amount: bigint): Promise<Hex> {
  if (!useSponsorship || !PAYMASTER_URL) {
    return fundInjectedGameAccount(amount);
  }

  if (!connectedAddress || !CONTRACTS.usdc || !CONTRACTS.mineOrDie) {
    throw new Error('Wallet not connected or contracts not configured');
  }

  // Check allowance
  const allowance = await getInjectedMineOrDieAllowance();

  if (allowance < amount) {
    // Try to batch approve + fund
    try {
      const approveData = encodeFunctionData({
        abi: USDC_ABI,
        functionName: 'approve',
        args: [CONTRACTS.mineOrDie, amount],
      });

      const fundData = encodeFunctionData({
        abi: MINE_OR_DIE_ABI,
        functionName: 'fund',
        args: [amount],
      });

      const result = await activeProvider!.request({
        method: 'wallet_sendCalls',
        params: [{
          version: '1.0',
          from: connectedAddress,
          calls: [
            { to: CONTRACTS.usdc, data: approveData, value: '0x0' },
            { to: CONTRACTS.mineOrDie, data: fundData, value: '0x0' },
          ],
          capabilities: {
            paymasterService: {
              url: PAYMASTER_URL,
            },
          },
        }],
      });

      const hash = typeof result === 'string' ? result : (result as any).id;
      console.log('Sponsored batch (approve + fund):', hash);

      if (publicClient) {
        await publicClient.waitForTransactionReceipt({ hash: hash as Hex });
      }

      return hash as Hex;
    } catch (e) {
      console.warn('Batch sponsorship failed, trying sequential:', e);
    }

    // Sequential fallback
    const approveData = encodeFunctionData({
      abi: USDC_ABI,
      functionName: 'approve',
      args: [CONTRACTS.mineOrDie, amount],
    });
    await sendSponsoredCall(CONTRACTS.usdc, approveData);
  }

  // Fund
  const fundData = encodeFunctionData({
    abi: MINE_OR_DIE_ABI,
    functionName: 'fund',
    args: [amount],
  });

  return sendSponsoredCall(CONTRACTS.mineOrDie, fundData);
}

/**
 * Withdraw USDC from game account
 */
export async function withdrawInjectedFromGame(amount: bigint): Promise<Hex> {
  if (!walletClient || !connectedAddress || !CONTRACTS.mineOrDie || !currentChain) {
    throw new Error('Wallet not connected');
  }

  const hash = await walletClient.writeContract({
    chain: currentChain,
    account: connectedAddress,
    address: CONTRACTS.mineOrDie,
    abi: MINE_OR_DIE_ABI,
    functionName: 'withdraw',
    args: [amount],
  });

  console.log('Withdraw tx:', hash);

  if (publicClient) {
    await publicClient.waitForTransactionReceipt({ hash });
  }

  return hash;
}

/**
 * Withdraw with gas sponsorship (if enabled)
 */
export async function withdrawInjectedFromGameSponsored(amount: bigint): Promise<Hex> {
  if (!useSponsorship || !PAYMASTER_URL || !CONTRACTS.mineOrDie) {
    return withdrawInjectedFromGame(amount);
  }

  const withdrawData = encodeFunctionData({
    abi: MINE_OR_DIE_ABI,
    functionName: 'withdraw',
    args: [amount],
  });

  return sendSponsoredCall(CONTRACTS.mineOrDie, withdrawData);
}

/**
 * Check if player can play (has enough funded balance)
 */
export async function canPlayInjected(wagerAmount: bigint = ENTRY_FEE): Promise<boolean> {
  const available = await getInjectedAvailableBalance();
  return available >= wagerAmount;
}

// ============ Legacy exports for backwards compatibility ============

/** @deprecated Use fundInjectedGameAccount instead */
export async function stakeToMatchInjected(_escrowAddress: Address): Promise<Hex> {
  const available = await getInjectedAvailableBalance();
  if (available < ENTRY_FEE) {
    return await fundInjectedGameAccount(ENTRY_FEE);
  }
  return '0x' as Hex;
}

/** @deprecated Use withdrawInjectedFromGame instead */
export async function claimRewardsInjected(_escrowAddress: Address): Promise<Hex> {
  const available = await getInjectedAvailableBalance();
  if (available > 0n) {
    return await withdrawInjectedFromGame(available);
  }
  return '0x' as Hex;
}

/** @deprecated Use canPlayInjected instead */
export async function hasInjectedWalletStaked(_escrowAddress: Address): Promise<boolean> {
  return await canPlayInjected();
}

/** @deprecated Use getInjectedAvailableBalance instead */
export async function getInjectedWalletClaimableReward(_escrowAddress: Address): Promise<bigint> {
  return await getInjectedAvailableBalance();
}

/** @deprecated Use fundInjectedGameAccountSponsored instead */
export async function stakeToMatchInjectedSponsored(escrowAddress: Address): Promise<Hex> {
  return stakeToMatchInjected(escrowAddress);
}

/** @deprecated Use withdrawInjectedFromGameSponsored instead */
export async function claimRewardsInjectedSponsored(escrowAddress: Address): Promise<Hex> {
  return claimRewardsInjected(escrowAddress);
}

/** @deprecated No longer needed - settlement handled by server */
export async function isMatchSettledInjected(_escrowAddress: Address): Promise<boolean> {
  return true;
}

/** @deprecated No longer needed - settlement handled by server */
export interface SettlementSignature {
  winners: Address[];
  amounts: bigint[];
  v: number;
  r: Hex;
  s: Hex;
}

/** @deprecated No longer needed - settlement handled by server */
export async function settleMatchInjected(
  _escrowAddress: Address,
  _settlement: SettlementSignature
): Promise<Hex> {
  return '0x' as Hex;
}

/** @deprecated Use getInjectedMineOrDieAllowance instead */
export async function getInjectedWalletAllowance(_escrowAddress: Address): Promise<bigint> {
  return getInjectedMineOrDieAllowance();
}

/** @deprecated Use approveUSDCForMineOrDie instead */
export async function approveUSDCInjected(_escrowAddress: Address): Promise<Hex> {
  return approveUSDCForMineOrDie(ENTRY_FEE);
}
